import { secureStorage } from 'utils/secureStorage';

import React, { ReactNode, useState, useEffect } from 'react';
import { Button, Col, Form, Image } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import AuthLeftPanel from '../Common/AuthLeftPanel';
import AuthHeadingPanel from '../Common/AuthHeadingPanel';
import { useFormik } from 'formik';
import { companySignupInitialValues, companySignupSchema } from 'constants/schemaValidations';
import { CompanySignupInterface } from 'constants/interfaces';
import {
	logEmailSignUp,
	useBucketUrlMutation,
	useCountryListQuery,
	usePageVisitMutation,
	useSignupMutation,
	useUploadFileMutation,
} from 'services';
import { USER_TYPES } from 'constants/index';
import toast from 'react-hot-toast';
import CustomSearchAddress from 'components/Common/CustomSearchAddress';
import useTranslate from 'hooks/useTranslate';
import { getPhoneFormat, imageBucketUrl } from 'utils';
import useReCaptchaV3 from 'hooks/reCaptchaV3';
import { setModalConfig } from 'store/common';

const CompanySignUp: React.FC = () => {
	const { translate } = useTranslate();
	const navigate = useNavigate();
	const location = useLocation();
	const { executeReCaptcha, isReady, error } = useReCaptchaV3(
		import.meta.env.VITE_RECAPTCHA_SITE_KEY,
	);
	const { mutateAsync: bucketUrlMutation } = useBucketUrlMutation();
	const { mutateAsync: uploadFileMutation } = useUploadFileMutation();
	const [selectedImage, setSelectedImage] = useState<string | null>(null);

	// mutation
	const { mutateAsync: signupMutation, isPending } = useSignupMutation();

	const [showPassword, setShowPassword] = useState({
		password: false,
		confirmPassword: false,
	});

	const { mutateAsync: pageVisitMutation } = usePageVisitMutation();
	let pageType = secureStorage.getItem('pageVisit');
	const ipAddress = secureStorage.getItem('ipAddress') ?? '';

	const togglePasswordVisibility = (field: 'password' | 'confirmPassword') => {
		setShowPassword((prev) => ({
			...prev,
			[field]: !prev[field],
		}));
	};

	const formik = useFormik({
		initialValues: { ...companySignupInitialValues },
		validationSchema: companySignupSchema(translate),
		onSubmit: async (values) => {
			const reCaptchaToken = await executeReCaptcha('signup');
			let profileImage = '';
			if (values?.profilePicture) {
				const file: any = values?.profilePicture;
				const imgPath = await imageBucketUrl(
					file,
					translate,
					file?.type,
					file.size,
					bucketUrlMutation,
					uploadFileMutation,
					'PROFILE_IMAGE',
				);
				if (imgPath) {
					profileImage = imgPath;
				}
			}

			signupMutation({
				...values,
				profilePicture: profileImage,
				userType: USER_TYPES.COMPANY,
				token: reCaptchaToken,
			})
				.then((res: any) => {
					if (res?.success) {
						toast.success(res?.message);
						navigate(`/login`);
					} else {
						toast.error(res?.message || 'Signup failed. Please try again.'); // Handle unsuccessful response
					}
				})
				.catch((error) => {
					console.error('Signup mutation error:', error);
					toast.error(translate('ToastMessage.anErrorOccurredDuringSignup')); // Notify the user of the error
				})
				.finally(() => {
					logEmailSignUp();
					if (!ipAddress) return;
					const payload = {
						type: pageType === 'home_b' ? 'startpage_b_signup' : 'startpage_a_signup',
						ipAddress: ipAddress,
					};
					pageVisitMutation(payload);
				});
		},
	});

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const email = params.get('email');
		if (email) {
			formik.setFieldValue('email', email);
		}
	}, [location.search]);

	const renderError = <T extends keyof CompanySignupInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	const onHideModal = () => {
		setModalConfig({
			visible: false,
			id: null,
			type: '',
		});
	};

	const handleImageUpload = (fileData: any) => {
		formik.setFieldValue('profilePicture', fileData);
		if (fileData) {
			const reader = new FileReader();
			reader.onload = () => {
				setSelectedImage(reader.result as string);
			};
			reader.readAsDataURL(fileData);
		} else {
			formik.setFieldValue('profilePicture', '');
			setSelectedImage('');
		}

		onHideModal();
	};

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			const reader = new FileReader();
			reader.onload = () => {
				setModalConfig({
					visible: true,
					type: 'signUpProfileCropper',
					onClose: onHideModal,
					data: {
						defaultImage: reader.result as string,
						updateImageData: handleImageUpload,
					},
				});
			};
			reader.readAsDataURL(file);
		} else {
			formik.setFieldValue('profilePicture', '');
			setSelectedImage('');
		}
	};

	return (
		<div className="auth bg-white d-flex flex-column flex-lg-row">
			{/* <div className="auth-left-content">
				<AuthLeftPanel />
			</div> */}
			<div className="auth-right-content flex-1 d-flex align-items-center justify-content-center position-relative">
				<div className="auth-form mb-0 mt-5 mx-3 my-5 m-md-5">
					<AuthHeadingPanel description={translate('Auth.authHeadingSignupPanelDescription')} />

					<div className="auth-form-fields">
						{selectedImage && (
							<Col md="12" className="text-center mb-4">
								<Image
									thumbnail
									style={{ width: '150px', height: '150px' }}
									src={selectedImage}
									alt="Uploaded Image"
									className="object-fit-cover"
									loading="lazy"
								/>
							</Col>
						)}
						<Form className="form" onSubmit={formik.handleSubmit}>
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Resume.uploadLogo')}*</Form.Label>
								<Form.Control
									type="file"
									placeholder={translate('Resume.uploadLogo')}
									className="rounded-pill"
									name="profilePicture"
									accept="image/*"
									onChange={handleImageChange}
									onBlur={formik.handleBlur}
								/>
								{renderError('profilePicture')}
							</Form.Group>
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('PlaceHolder.companyName')}*</Form.Label>
								<Form.Control
									type="text"
									placeholder={translate('PlaceHolder.companyName')}
									name="companyName"
									className="rounded-pill"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.companyName}
									autoComplete="off"
								/>
								{renderError('companyName')}
							</Form.Group>
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('PlaceHolder.email')}*</Form.Label>
								<Form.Control
									type="email"
									placeholder={translate('PlaceHolder.emailText')}
									name="email"
									className="rounded-pill"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.email}
									autoComplete="off"
								/>
								{renderError('email')}
							</Form.Group>
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Profile.editCompanyUrl')}*</Form.Label>
								<Form.Control
									type="text"
									placeholder={translate('PlaceHolder.enterCompanyUrl')}
									className="rounded-pill"
									name="websiteLink"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.websiteLink}
									autoComplete="off"
								/>
								{renderError('websiteLink')}
							</Form.Group>

							<Form.Group className="mb-4 position-relative custom-search-address">
								<Form.Label className="lh-1">{translate('PlaceHolder.location')}*</Form.Label>
								<CustomSearchAddress
									selectProps={{
										placeholder: translate('PlaceHolder.locationText'),
										onChange: (value: any) => {
											formik.setFieldValue('address', value.location);
											formik.setFieldValue('cityId', value.cityId);
											formik.setFieldValue('stateId', value.stateId);
											formik.setFieldValue('countryId', value.countryId);
										},
										name: 'address',
									}}
									fieldValue={formik.values.address}
									onBlur={formik.handleBlur}
								/>
								<i className="ri-search-line form-icon position-absolute lh-1"></i>
								{renderError('address') ||
									renderError('cityId') ||
									renderError('stateId') ||
									renderError('countryId')}
							</Form.Group>
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('PlaceHolder.password')}*</Form.Label>
								<Form.Control
									type={showPassword.password ? 'text' : 'password'}
									name="password"
									placeholder={translate('PlaceHolder.passwordText', { field: '8' })}
									className="rounded-pill"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.password}
									autoComplete="off"
								/>
								<i
									className={`ri-eye${!showPassword.password ? '-off' : ''}-line form-icon position-absolute lh-1`}
									onClick={() => togglePasswordVisibility('password')}
								></i>
								{renderError('password')}
							</Form.Group>
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">
									{translate('PlaceHolder.confirmPassword')}*
								</Form.Label>
								<Form.Control
									type={showPassword.confirmPassword ? 'text' : 'password'}
									name="confirmPassword"
									placeholder={translate('PlaceHolder.passwordText', { field: '8' })}
									className="rounded-pill"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.confirmPassword}
									autoComplete="off"
								/>
								<i
									className={`ri-eye${!showPassword.confirmPassword ? '-off' : ''}-line form-icon position-absolute lh-1`}
									onClick={() => togglePasswordVisibility('confirmPassword')}
								></i>
								{renderError('confirmPassword')}
							</Form.Group>
							<div className="d-flex align-items-center justify-content-between position-relative mb-3">
								<Form.Check
									type="checkbox"
									name="agreeTerms"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									checked={formik.values.agreeTerms}
									id="AgreetermsConditionsCompany"
									label={
										<>
											{translate('Common.iAgree')}{' '}
											<Link to="/terms-conditions">{translate('Common.t&C')}</Link>{' '}
											{translate('Common.and')}{' '}
											<Link to="/privacy-policy">{translate('Common.privacyPolicy')}</Link>
										</>
									}
								></Form.Check>
								{renderError('agreeTerms')}
							</div>
							{/* <div className="d-flex align-items-center justify-content-between position-relative mb-4">
								<Form.Check
									type="checkbox"
									name="newsletter"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									checked={formik.values.newsletter}
									id="newsletter"
									label={translate('Common.newsletterText')}
								></Form.Check>
							</div> */}
							<div className="form-btn">
								<Button
									type="submit"
									className="filled-btn rounded-pill w-100"
									disabled={isPending}
								>
									{translate('Common.registerNow')}
								</Button>
							</div>
							<p className="mb-0 text-center">
								{translate('Common.alreadyHaveAccount')}{' '}
								<Link to={`/login?userType=${USER_TYPES.COMPANY}`} className="fw-bold join-link">
									{translate('Common.loginNow')}
								</Link>
							</p>
						</Form>
					</div>
				</div>
			</div>
		</div>
	);
};

export default CompanySignUp;
